"use client";

import { IconButton, Menu, MenuItem, Typography } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getStoredLocale, setStoredLocale, normalizeLocaleForContext } from "@/utils/localeCookie";

export default function LanguageSelectorCADA() {
    const router = useRouter();
    const { t } = useTranslation();
    const [currentLocale, setCurrentLocale] = useState<"en" | "fr">("en");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        // Only allow language switching on CADA
        const hostname = window.location.hostname;
        if (!hostname.includes("cada")) {
            setCurrentLocale("en");
            return;
        }

        // Check current locale from cookie
        const storedLocale = getStoredLocale();
        if (storedLocale) {
            setCurrentLocale(normalizeLocaleForContext(storedLocale));
        } else {
            // Detect browser language if no cookie is set
            const browserLang = navigator.language.toLowerCase();
            const detectedLocale = browserLang.startsWith("fr") ? "fr" : "en";
            setCurrentLocale(detectedLocale);
            // Set the detected locale as cookie (fr-CA format)
            const cookieLocale = detectedLocale === "fr" ? "fr-CA" : "en";
            setStoredLocale(cookieLocale);
        }

        // Listen for locale changes from other components
        const handleLocaleChange = (e: CustomEvent<{ locale: string }>) => {
            setCurrentLocale(normalizeLocaleForContext(e.detail.locale));
        };

        window.addEventListener("localeChange", handleLocaleChange as EventListener);
        return () => {
            window.removeEventListener("localeChange", handleLocaleChange as EventListener);
        };
    }, []);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const switchLanguage = (locale: "en" | "fr") => {
        // Map to cookie format (fr -> fr-CA)
        const cookieLocale = locale === "fr" ? "fr-CA" : "en";

        setStoredLocale(cookieLocale); // This dispatches localeChange event
        setCurrentLocale(locale);
        handleClose();

        // Refresh to apply new locale
        router.refresh();
    };

    const getLanguageLabel = () => {
        return currentLocale === "en" ? "EN" : "FR";
    };

    return (
        <>
            <IconButton
                onClick={handleClick}
                size="medium"
                aria-label={t("ui.languageSelector.ariaLabel")}
                aria-controls={open ? "language-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                    color: "#1976d2",
                    bgcolor: "white",
                    border: "2px solid #1976d2",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                        bgcolor: "#f5f5f5",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                    gap: 0.5,
                    minWidth: 80,
                }}
            >
                <LanguageIcon fontSize="small" />
                <Typography variant="button" sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
                    {getLanguageLabel()}
                </Typography>
            </IconButton>
            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "language-button",
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                sx={{ mt: 1 }}
            >
                <MenuItem
                    onClick={() => switchLanguage("en")}
                    selected={currentLocale === "en"}
                    sx={{ minWidth: 150, display: "flex", justifyContent: "space-between", gap: 2 }}
                >
                    <Typography variant="body2">English</Typography>
                    {currentLocale === "en" && <CheckIcon fontSize="small" />}
                </MenuItem>
                <MenuItem
                    onClick={() => switchLanguage("fr")}
                    selected={currentLocale === "fr"}
                    sx={{ minWidth: 150, display: "flex", justifyContent: "space-between", gap: 2 }}
                >
                    <Typography variant="body2">Français</Typography>
                    {currentLocale === "fr" && <CheckIcon fontSize="small" />}
                </MenuItem>
            </Menu>
        </>
    );
}
